/**
 * Per-section resume optimization.
 *
 * LLM routing (in priority order):
 *  1. Ollama local — when VITE_USE_OLLAMA=true and Ollama is reachable.
 *  2. Supabase optimize-resume edge — Gemini on server (no browser API keys).
 */

import { getOutputLanguageName, normalizeDocumentLocale } from '../modules/ai-agent/utils/documentLanguage';
import {
  RESUME_LAYOUT_GUARDRAILS,
  RESUME_LAYOUT_TEMPERATURE,
  RESUME_OUTPUT_STRUCTURE_DIRECTIVE,
  RESUME_SECTION_BLOCK_FORMAT,
  resumeSectionMaxTokens,
  sanitizeResumeLlmOutput,
  stripResumeSectionFence,
} from '../modules/ai-agent/resumeLayoutGuardrails';
import { RESUME_OPTIMIZE_MODEL } from '../modules/ai-agent/agentSession';
import { ledger } from './creditLedgerService';
import { supabase } from '../shared/services/api/supabase';

const USE_OLLAMA   = (import.meta.env.VITE_USE_OLLAMA  as string | undefined) === 'true';
const OLLAMA_URL   = (import.meta.env.VITE_OLLAMA_URL  as string | undefined) ?? 'http://localhost:11434';
const OLLAMA_MODEL = (import.meta.env.VITE_OLLAMA_MODEL as string | undefined) ?? 'gpt-oss:20b';
const GROQ_API_KEY = (import.meta.env.VITE_GROQ_API_KEY as string | undefined) ?? '';
const OPENAI_API_KEY = (import.meta.env.VITE_OPENAI_API_KEY as string | undefined) ?? '';
const OPENAI_SECTION_MODEL =
  (import.meta.env.VITE_OPENAI_AGENT_MODEL as string | undefined)?.trim() || 'gpt-4o-mini';
/**
 * Section optimization model.
 * llama-3.3-70b-versatile: best quality, follows complex instructions precisely.
 * Section prompts are ~2000 tokens (no chat history) — well within 12k TPM limit.
 * Falls back to 8b only if the 70b call fails with a rate-limit error.
 */
const GROQ_SECTION_MODEL         = 'llama-3.3-70b-versatile';
const GROQ_SECTION_MODEL_FALLBACK = 'llama-3.1-8b-instant';

export interface SectionInput {
  title: string;
  content: string;
}

export interface JobContext {
  title?: string;
  company?: string;
  description?: string;
  skills?: string[];
  requirements?: string[];
}

/** Optional context so the LLM can produce unique, candidate-specific output. */
export interface ResumeContext {
  candidateName?: string;
  /** Short summary of the rest of the resume (other section titles or first N chars) to avoid generic phrasing. */
  fullResumeSummary?: string;
  /** When true, generate content from scratch instead of optimizing existing content. */
  generateMode?: boolean;
  /** Known skills to incorporate when generating content. */
  skills?: string[];
  /** Candidate's current role / headline for context. */
  headline?: string;
  /** Years of experience. */
  experienceYears?: number;
  /** Pre-filled education lines from profile (skips generic placeholders). */
  educationSeed?: string;
  /** Structured work history — injected into edge prompts so employers are never placeholders. */
  experience?: Array<{
    title?: string;
    company?: string;
    location?: string;
    startDate?: string;
    endDate?: string;
    bullets?: string[];
  }>;
  /** Keep employers, titles, dates; only add depth to existing bullets. */
  preserveExistingContent?: boolean;
  /** Target output language for section body (en | es | pt | ru). Defaults to English. */
  outputLanguage?: string;
}

export interface SectionResult {
  title: string;
  originalContent: string;
  optimizedContent: string;
  skipped?: boolean;
  /** Set when optimization failed — UI can surface this to the user. */
  error?: string;
}

// Only these sections benefit from LLM rewrites; others are formatted locally
const LLM_SECTIONS = new Set([
  'professional summary', 'summary', 'objective', 'career summary',
  'work experience', 'experience', 'professional experience', 'employment history', 'career history',
  'skills', 'technical skills', 'key skills', 'core competencies', 'competencies',
  'projects', 'projects & achievements', 'key projects', 'project',
  'education', 'academic background', 'qualifications',
  'certifications', 'certificates', 'licenses',
]);

function needsLLM(title: string): boolean {
  const t = title.toLowerCase().trim();
  if (LLM_SECTIONS.has(t)) return true;
  return (
    /\b(summary|objective|experience|employment|skills?|competenc|projects?|education|certif)/i.test(t)
  );
}

function localFormatSection(section: SectionInput): string {
  return section.content
    .split('\n')
    .map(l => {
      const t = l.trim();
      if (!t) return '';
      if (t.startsWith('- ') || t.startsWith('* ')) return '• ' + t.slice(2);
      return t;
    })
    .join('\n')
    .trim();
}

/**
 * When no existing content is available for a section, build a rich seed prompt
 * that the `section_optimize` handler can work with to generate meaningful output.
 * 
 * CRITICAL: Include full JD context so the LLM can write ATS-optimized content
 * that matches the job requirements.
 */
function buildGenerationSeed(
  sectionTitle: string,
  job: JobContext,
  ctx: ResumeContext,
): string {
  const name = ctx.candidateName || 'Candidate';
  const headline = ctx.headline || job.title || '';
  const skills = (ctx.skills || []).slice(0, 25).join(', ');
  const yoe = ctx.experienceYears ? `${ctx.experienceYears} years of experience` : '';
  const titleLower = sectionTitle.toLowerCase();
  
  // Include JD context for ATS optimization
  const jdSnippet = (job.description || '').slice(0, 1500);
  const jobSkills = (job.skills || []).join(', ');
  const jobReqs = (job.requirements || []).join('; ');

  if (titleLower.includes('summary') || titleLower === 'objective') {
    // Build genuine prose that section_optimize can rewrite into a strong summary.
    // Do NOT use raw profile skills (they contain category-header fragments).
    // Instead, use the job title, JD skills, and requirements as the basis.
    const topJdSkills = (job.skills || []).slice(0, 5).join(', ') || 'relevant technologies';
    const topJdReq = (job.requirements || []).slice(0, 2).join(' and ') || 'delivering results';
    return [
      headline
        ? `${headline}${yoe ? ` with ${yoe} of experience` : ''}.`
        : `Experienced professional${yoe ? ` with ${yoe}` : ''}.`,
      `Skilled in ${topJdSkills}.`,
      `Proven ability to ${topJdReq}.`,
      `Seeking a ${job.title || 'senior'} role${job.company ? ` at ${job.company}` : ''} to drive measurable business outcomes.`,
    ].join(' ');
  }

  if (titleLower.includes('experience') || titleLower.includes('employment')) {
    // Return a clear signal that no real experience data exists — the LLM
    // should generate a generic placeholder rather than hallucinating employer history.
    // We intentionally do NOT include the target company or any fake employer name.
    return [
      `[NO EXPERIENCE DATA AVAILABLE]`,
      `Candidate Name: ${name}`,
      headline ? `Headline: ${headline}` : '',
      yoe ? `Estimated experience: ${yoe}` : '',
      `Relevant skills: ${skills.slice(0, 80) || 'to be provided'}`,
      ``,
      `Please generate 1-2 realistic experience entries with complete role titles, company names, and date ranges.`,
      `Do NOT invent real company names or real dates. Never output [Company Name] or [+ WORK_EXPERIENCE] tokens in the final section.`,
    ].filter(Boolean).join('\n');
  }

  if (titleLower.includes('education') || titleLower.includes('qualification')) {
    const eduLines = ctx.educationSeed;
    if (eduLines) return eduLines;
    return [
      `Write 1–2 complete education entries with REAL institution names and degree titles.`,
      `Candidate: ${name}. Target role: ${job.title || 'professional role'}.`,
      `Use plausible Indian or UK institutions if none provided. NO bracket placeholders like [Degree Name].`,
      jdSnippet ? `Job context: ${jdSnippet.slice(0, 400)}` : '',
    ].filter(Boolean).join('\n');
  }

  if (titleLower.includes('project')) {
    return [
      `Generate 2 project entries with title, 2–3 bullet achievements, and technologies.`,
      `Candidate: ${name}. Role context: ${headline || job.title || 'professional'}.`,
      `NO [Project Name] or [Company] placeholders — use realistic project titles for the candidate's field.`,
      jobSkills ? `Relevant skills: ${jobSkills}` : '',
    ].filter(Boolean).join('\n');
  }

  if (titleLower.includes('skill')) {
    // Use JD skills + clean individual profile skills (not raw category-header strings)
    const cleanProfileSkills = (ctx.skills || [])
      .filter((s) => !s.includes(':') && !s.endsWith('&') && s.length >= 3 && s.length <= 35)
      .slice(0, 20);
    const allSkills = [...new Set([...cleanProfileSkills, ...(job.skills || [])])];

    // Detect role level to decide on bucketed vs flat output
    const combinedCtx = `${job.title || ''} ${ctx.headline || ''}`.toLowerCase();
    const isSenior = /senior|lead|principal|director|head|vp |vice president|manager|executive|chief/i.test(combinedCtx);

    if (isSenior) {
      // Return a pre-bucketed seed so the LLM reinforces the structure
      const productSkills = allSkills.filter((s) =>
        /product|roadmap|strategy|okr|kpi|agile|scrum|safe|jira|figma|miro|balsamiq|ux|market|analytics|stakeholder/i.test(s)
      );
      const techSkills = allSkills.filter((s) =>
        /java|python|go|bash|spring|kubernetes|docker|kafka|rest|api|aws|gcp|azure|sql|ml|ai|llm|rag|transformer|langchain/i.test(s)
      );
      const leadershipSkills = allSkills.filter((s) =>
        /leadership|mentor|team|cross.functional|presentation|communication|hiring|budget|p&l|negotiat/i.test(s)
      );
      const remaining = allSkills.filter((s) => !productSkills.includes(s) && !techSkills.includes(s) && !leadershipSkills.includes(s));

      return [
        '### Product & Strategy',
        (productSkills.length ? productSkills : remaining.slice(0, 6)).join(', '),
        '',
        '### Technical & Domain Expertise',
        (techSkills.length ? techSkills : remaining.slice(6, 14)).join(', '),
        '',
        '### Leadership & Execution',
        (leadershipSkills.length ? leadershipSkills : remaining.slice(14, 20)).join(', '),
      ].join('\n');
    }

    return `Technical Skills: ${allSkills.join(', ') || 'Software Development, Problem Solving, Communication'}`;
  }

  return `${name}. Target role: ${job.title}. Skills: ${skills}. Generate ${sectionTitle} content.`;
}

// ── Broken-format experience pre-processor ────────────────────────────────────
// PDF parsers often produce experience content where each line mixes a date range
// with a bullet: "• Apr 2017 - Mar 2019 • Responsible for providing knowledge..."
// This function detects that pattern and reconstructs proper job blocks so the
// LLM receives structured input it can cleanly rewrite.
const DATE_TOKEN = /(\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b\s+\d{4}|\d{1,2}\/\d{4}|\d{4})/i;
const DATE_RANGE_RE = new RegExp(
  `${DATE_TOKEN.source}\\s*[-–—,]\\s*(?:${DATE_TOKEN.source}|Present|Current)`,
  'i',
);
const INLINE_DATE_BULLET_RE = new RegExp(
  `^[•\\-*]?\\s*(${DATE_TOKEN.source}\\s*[-–—,]\\s*(?:${DATE_TOKEN.source}|Present|Current))\\s*[•·]\\s*(.+)`,
  'i',
);

function preprocessBrokenExperienceContent(content: string): string {
  const raw = content.replace(/\r\n/g, '\n').trim();
  const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);

  // Only transform if we detect the "date inline with bullet" pattern
  const hasInlineDateBullets = lines.some(l => INLINE_DATE_BULLET_RE.test(l));
  if (!hasInlineDateBullets) return content;

  type JobBlock = { dateRange: string; bullets: string[]; company?: string };
  const blocks: JobBlock[] = [];
  let pendingCompany: string | null = null;

  for (const line of lines) {
    // Pattern: "• Apr 2017 - Mar 2019 • Responsible for..."
    const inlineMatch = line.match(INLINE_DATE_BULLET_RE);
    if (inlineMatch) {
      const dateRange = inlineMatch[1].trim().replace(/,\s*/, ' – ');
      // Groups: [1] full date range, [2] start token, [3] end token, [4] bullet text
      const bulletText = (inlineMatch[4] ?? inlineMatch[3] ?? '').trim();
      const last = blocks[blocks.length - 1];
      if (last && last.dateRange === dateRange) {
        last.bullets.push(bulletText);
      } else {
        blocks.push({ dateRange, bullets: [bulletText], company: pendingCompany ?? undefined });
        pendingCompany = null;
      }
      continue;
    }

    // Pattern: "CompanyName | Jan 2016 – Mar 2017 • bullet" (first line, header mixed with bullet)
    const headerWithDateBullet = line.match(
      /^([^|•]+)\s*\|\s*(.*?)\s*[•·]\s*(.+)$/,
    );
    if (headerWithDateBullet) {
      const possibleHeader = headerWithDateBullet[1].trim();
      const rest = headerWithDateBullet[2].trim();
      const bulletText = headerWithDateBullet[3].trim();
      const dateInRest = rest.match(DATE_RANGE_RE);
      if (dateInRest) {
        const dateRange = dateInRest[0].replace(/,\s*/, ' – ');
        // possibleHeader is probably the company or job title (strip markdown **)
        const company = possibleHeader.replace(/\*+/g, '').trim() || '[Company Name]';
        blocks.push({ dateRange, bullets: [bulletText], company });
        continue;
      }
    }

    // A plain company/header line with no bullets — hold it for the next block
    if (!DATE_RANGE_RE.test(line) && !isBulletLineStr(line)) {
      const cleaned = line.replace(/\*+/g, '').trim();
      if (cleaned && cleaned.length < 80) pendingCompany = cleaned;
    }
  }

  if (blocks.length === 0) return content;

  // Sort blocks by start date descending (most recent first)
  blocks.sort((a, b) => {
    const extractYear = (dr: string) => {
      const m = dr.match(/(\d{4})/g);
      return m ? parseInt(m[m.length - 1], 10) : 0;
    };
    return extractYear(b.dateRange) - extractYear(a.dateRange);
  });

  return blocks.map(b => {
    const company = b.company || '';
    const title = 'Role';
    return [
      `**${title}** | ${company || 'Company'}`,
      b.dateRange,
      ...b.bullets.map(bt => `• ${bt}`),
    ].join('\n');
  }).join('\n\n');
}

function isBulletLineStr(line: string): boolean {
  return /^[•\-*]\s/.test(line.trim());
}

/**
 * Check Ollama reachability and resolve the best available model.
 * Returns null if Ollama is offline or CORS blocked.
 * Uses the same resolution logic as the chat agent:
 *   1. Exact match for VITE_OLLAMA_MODEL
 *   2. Prefix match
 *   3. First available model in the list
 */
async function resolveOllamaModel(): Promise<string | null> {
  try {
    const ctrl  = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 3_000);
    const res   = await fetch(`${OLLAMA_URL}/api/tags`, { signal: ctrl.signal });
    clearTimeout(timer);
    if (!res.ok) return null;

    const data  = (await res.json()) as { models?: { name: string }[] };
    const names = (data.models ?? []).map((m) => m.name);
    if (names.length === 0) return null;

    // Prefer the configured model; fall back to first available
    const preferred = OLLAMA_MODEL.split(':')[0];
    const resolved  =
      names.find((n) => n === OLLAMA_MODEL) ??
      names.find((n) => n.startsWith(preferred)) ??
      names[0];

    console.info(`[SectionOptimize] resolved Ollama model: ${resolved} (preferred: ${OLLAMA_MODEL})`);
    return resolved;
  } catch {
    return null;
  }
}

/**
 * Build the system prompt for a local Ollama section rewrite.
 * Much more focused than the general agent system prompt — purely for
 * resume content rewriting with no job-search or action-tag rules.
 */
function buildOllamaOptimizePrompt(
  section: SectionInput,
  job: JobContext,
  ctx: ResumeContext | undefined,
  instruction: string | undefined,
): { system: string; user: string } {
  const jd = (job.description || '').slice(0, 2000);
  const skills = (job.skills || []).join(', ');
  const name = ctx?.candidateName || 'Candidate';
  const headline = ctx?.headline || job.title || '';

  // ── Stylistic variation — picked randomly per call so each re-optimization
  // produces a different angle/voice, not identical phrasing. Combined with
  // temperature + seed, this is what makes the rewrite feel fresh each time.
  const STYLE_HINTS = [
    'Lead with quantifiable impact and metrics (revenue, %, scale, team size).',
    'Emphasize leadership, cross-functional collaboration, and stakeholder management.',
    'Highlight technical depth and tools mastered relevant to the JD.',
    'Focus on strategic outcomes and business value delivered.',
    'Front-load ATS keywords from the JD without keyword-stuffing.',
    'Use the "Challenge → Action → Result" pattern in each bullet.',
    'Emphasize scope and scale — team size managed, budget owned, users impacted.',
    'Highlight innovation — new processes, tools, or approaches introduced.',
  ];
  const preserve = Boolean(ctx?.preserveExistingContent);
  const styleHint = preserve
    ? 'Add 1–2 stronger bullets per role OR expand existing bullets with metrics — do not delete roles.'
    : STYLE_HINTS[Math.floor(Math.random() * STYLE_HINTS.length)];

  const docLocale = normalizeDocumentLocale(ctx?.outputLanguage);
  const langName = getOutputLanguageName(docLocale);

  const system = [
    preserve
      ? `You are an expert resume editor. EXPAND the existing "${section.title}" section — do NOT replace it.`
      : `You are an expert resume writer. Rewrite the "${section.title}" section of a resume.`,
    `Rules:`,
    `- CRITICAL: Write the entire section body in ${langName} only. The document must be strictly monolingual.`,
    `- Output ONLY the section content. No headings, no intro, no "Here is the updated..." preamble.`,
    preserve
      ? `- MANDATORY: Keep EVERY employer name, job title, and date range from the original text exactly (same companies, same years).`
      : `- Keep the same structure (bullet points stay bullet points, prose stays prose).`,
    preserve
      ? `- MANDATORY: Do NOT invent new companies, new job titles, or new date ranges. Do NOT change the candidate's career track.`
      : `- Use strong action verbs. Quantify achievements where possible.`,
    preserve
      ? `- Add more detail: extra bullets or richer metrics on existing bullets. Bullet list format required.`
      : `- Incorporate relevant keywords from the job description for ATS optimization.`,
    `- Do NOT invent fake companies, dates, or degrees.`,
    `- NEVER use "Unknown" as a company name. Always use the REAL company name from the candidate's data.`,
    `- Do NOT include the section title in your output.`,
    `- NEVER output square-bracket placeholders like [Degree Name] or [Company] — use realistic text or omit.`,
    `- The user's instruction below is MANDATORY and overrides stylistic variation.`,
    preserve ? `- Focus: ${styleHint}` : `- This rewrite's stylistic focus: ${styleHint}`,
    instruction ? `- USER INSTRUCTION (highest priority): ${instruction}` : '',
    RESUME_OUTPUT_STRUCTURE_DIRECTIVE,
    RESUME_LAYOUT_GUARDRAILS,
    RESUME_SECTION_BLOCK_FORMAT,
  ].filter(Boolean).join('\n');

  // Build experience context so the LLM knows real company names
  const expContext = ctx?.experience?.length
    ? `\nCandidate's actual work history (use these EXACT company names, titles, and dates):\n${ctx.experience.map((e, i) => `${i + 1}. ${e.title ?? 'Role'} at ${e.company ?? 'N/A'} (${e.startDate ?? '?'} – ${e.endDate ?? 'Present'})`).join('\n')}`
    : '';

  const user = [
    `Candidate: ${name}${headline ? ` — ${headline}` : ''}`,
    job.title   ? `Target role: ${job.title}${job.company ? ` at ${job.company}` : ''}` : '',
    expContext,
    preserve ? '' : jd ? `Job description (for ATS keyword matching):\n${jd}` : '',
    preserve ? '' : skills ? `Key skills to include: ${skills}` : '',
    ``,
    preserve
      ? `EXISTING "${section.title}" — expand in place (same employers & dates):`
      : `Current "${section.title}" content to rewrite:`,
    `---`,
    section.content,
    `---`,
    instruction ? `Apply this user request exactly: ${instruction}` : '',
    preserve
      ? `Return the FULL section with the SAME roles/employers/dates, only with more detail added.`
      : `Rewrite this section now. Output only the improved section body:`,
  ].filter(s => s !== undefined && s !== '').join('\n');

  return { system, user };
}

/** Call Ollama /api/chat (non-streaming) for a section rewrite. */
async function optimizeSectionViaOllama(
  model: string,
  section: SectionInput,
  job: JobContext,
  signal?: AbortSignal,
  resumeContext?: ResumeContext,
  instruction?: string,
): Promise<SectionResult> {
  const { system, user } = buildOllamaOptimizePrompt(section, job, resumeContext, instruction);
  const preserve = Boolean(resumeContext?.preserveExistingContent);
  const maxTokens = resumeSectionMaxTokens(section.title);
  const temperature = preserve ? 0.25 : RESUME_LAYOUT_TEMPERATURE;

  const res = await fetch(`${OLLAMA_URL}/api/chat`, {
    method: 'POST',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model,
      stream:   false,
      messages: [
        { role: 'system', content: system },
        { role: 'user',   content: user   },
      ],
      options: {
        temperature,
        top_p:       preserve ? 0.9 : 0.85,
        num_predict: maxTokens,
        seed:        preserve ? undefined : Math.floor(Math.random() * 1_000_000),
      },
    }),
  });

  if (!res.ok) throw new Error(`Ollama ${res.status}: ${await res.text()}`);

  const data = await res.json() as { message?: { content?: string } };
  const optimized = stripResumeSectionFence((data.message?.content ?? '').trim());

  console.info(`[SectionOptimize] Ollama (${model}) rewrote "${section.title}" — ${optimized.length} chars`);

  return {
    title: section.title,
    originalContent: section.content,
    optimizedContent: optimized.length > 20 ? optimized : section.content,
  };
}

/**
 * Call Groq API directly from the browser for a section rewrite.
 * Uses llama-3.1-8b-instant (20k TPM free limit) — safe for resume+JD sized prompts.
 * This is the PRIMARY cloud path, bypassing the Supabase edge function black box.
 */
async function callGroqForSection(
  model: string,
  section: SectionInput,
  job: JobContext,
  signal?: AbortSignal,
  resumeContext?: ResumeContext,
  instruction?: string,
): Promise<string> {
  const { system, user } = buildOllamaOptimizePrompt(section, job, resumeContext, instruction);
  const preserve = Boolean(resumeContext?.preserveExistingContent);
  const temperature = preserve ? 0.25 : RESUME_LAYOUT_TEMPERATURE;
  const maxTokens = resumeSectionMaxTokens(section.title);

  const res = await fetch('https://api.groq.com/openai/v1/chat/completions', {
    method: 'POST',
    signal,
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${GROQ_API_KEY}`,
    },
    body: JSON.stringify({
      model,
      stream:      false,
      temperature,
      top_p:       preserve ? 0.9 : 0.85,
      max_tokens:  maxTokens,
      seed:        preserve ? undefined : Math.floor(Math.random() * 1_000_000),
      messages: [
        { role: 'system', content: system },
        { role: 'user',   content: user   },
      ],
    }),
  });

  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(`Groq ${res.status}: ${errText.slice(0, 200)}`);
  }

  const data = await res.json() as { choices?: Array<{ message?: { content?: string } }> };
  return stripResumeSectionFence((data.choices?.[0]?.message?.content ?? '').trim());
}

async function callOpenAIForSection(
  section: SectionInput,
  job: JobContext,
  signal?: AbortSignal,
  resumeContext?: ResumeContext,
  instruction?: string,
): Promise<string> {
  const { system, user } = buildOllamaOptimizePrompt(section, job, resumeContext, instruction);
  const preserve = Boolean(resumeContext?.preserveExistingContent);
  const temperature = preserve ? 0.25 : RESUME_LAYOUT_TEMPERATURE;
  const maxTokens = resumeSectionMaxTokens(section.title);

  const res = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${OPENAI_API_KEY}`,
    },
    body: JSON.stringify({
      model: OPENAI_SECTION_MODEL,
      temperature,
      max_tokens: maxTokens,
      messages: [
        { role: 'system', content: system },
        { role: 'user', content: user },
      ],
    }),
  });

  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(`OpenAI ${res.status}: ${errText.slice(0, 200)}`);
  }

  const data = await res.json() as { choices?: Array<{ message?: { content?: string } }> };
  return stripResumeSectionFence((data.choices?.[0]?.message?.content ?? '').trim());
}

/**
 * Call Groq directly for section optimization.
 * Primary: llama-3.3-70b-versatile (best quality, strong instruction following).
 * Fallback: llama-3.1-8b-instant (higher TPM, used only on 429 rate limit).
 */
async function optimizeSectionViaGroqDirect(
  section: SectionInput,
  job: JobContext,
  signal?: AbortSignal,
  resumeContext?: ResumeContext,
  instruction?: string,
): Promise<SectionResult> {
  let optimized = '';
  let modelUsed = GROQ_SECTION_MODEL;

  try {
    optimized = await callGroqForSection(GROQ_SECTION_MODEL, section, job, signal, resumeContext, instruction);
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    // Rate-limited on 70b → fall back to 8b automatically
    const msg = (err as Error).message ?? '';
    if (msg.includes('429') || msg.includes('rate_limit')) {
      console.warn('[SectionOptimize] 70b rate-limited, retrying with 8b fallback');
      modelUsed = GROQ_SECTION_MODEL_FALLBACK;
      try {
        optimized = await callGroqForSection(GROQ_SECTION_MODEL_FALLBACK, section, job, signal, resumeContext, instruction);
      } catch (fallbackErr) {
        if ((fallbackErr as Error).name === 'AbortError') throw fallbackErr;
        const fbMsg = (fallbackErr as Error).message ?? '';
        if (fbMsg.includes('429') || fbMsg.includes('rate_limit')) {
          if (OPENAI_API_KEY) {
            console.warn('[SectionOptimize] Groq exhausted, trying OpenAI fallback');
            optimized = await callOpenAIForSection(section, job, signal, resumeContext, instruction);
            modelUsed = OPENAI_SECTION_MODEL;
          } else {
            throw new Error(
              'Groq API daily limit reached. Wait for your quota to reset, or set VITE_OPENAI_API_KEY in .env.development.',
            );
          }
        } else {
          throw fallbackErr;
        }
      }
    } else {
      throw err;
    }
  }

  console.info(`[SectionOptimize] Groq direct (${modelUsed}) rewrote "${section.title}" — ${optimized.length} chars`);

  return {
    title:            section.title,
    originalContent:  section.content,
    optimizedContent: optimized.length > 20 ? optimized : section.content,
  };
}

/**
 * Optimise a single resume section.
 * Priority: Ollama → optimize-resume edge (Gemini on server).
 * @param instruction  Optional user instruction (e.g. "add more metrics", "make it comprehensive").
 */
export async function optimizeSection(
  section: SectionInput,
  job: JobContext,
  signal?: AbortSignal,
  resumeContext?: ResumeContext,
  instruction?: string,
): Promise<SectionResult> {
  if (!needsLLM(section.title)) {
    return {
      title: section.title,
      originalContent: section.content,
      optimizedContent: localFormatSection(section),
      skipped: true,
    };
  }

  const isGenMode    = resumeContext?.generateMode;
  const titleLower   = section.title.toLowerCase();
  const isExpSection = titleLower.includes('experience') || titleLower.includes('employment') || titleLower.includes('career');

  // Pre-process content once — used by all LLM paths below.
  let rawContent = isGenMode
    ? buildGenerationSeed(section.title, job, resumeContext!)
    : section.content;
  if (isExpSection && !isGenMode) {
    rawContent = preprocessBrokenExperienceContent(rawContent);
  }

  // ── 1. Ollama (local GPU) ─────────────────────────────────────────────────
  if (USE_OLLAMA) {
    try {
      const resolvedModel = await resolveOllamaModel();
      if (resolvedModel) {
        return await optimizeSectionViaOllama(
          resolvedModel,
          { title: section.title, content: rawContent },
          job, signal, resumeContext, instruction,
        );
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') throw err;
      console.warn('[SectionOptimize] Ollama failed, trying optimize-resume edge:', (err as Error).message);
    }
  }

  // ── 2. Supabase optimize-resume edge (Gemini on server) ─────────────────
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseKey) {
    return { title: section.title, originalContent: section.content, optimizedContent: section.content, skipped: true };
  }

  const effectiveContent = rawContent;

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      apikey: supabaseKey,
    };
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`;
    }

    const requestPayload = {
      useCase: 'section_optimize',
      model: RESUME_OPTIMIZE_MODEL,
      sectionTitle: section.title,
      sectionContent: effectiveContent,
      jobData: {
        title: job.title || '',
        company: job.company || '',
        // Only append user instruction — skill bucketing is now handled server-side
        description: [
          job.description || '',
          instruction ? `\n\nUSER INSTRUCTION (apply when rewriting): ${instruction}` : '',
        ].filter(Boolean).join('').trim(),
        skills: job.skills || [],
        requirements: job.requirements || [],
      },
      resumeContext: resumeContext
        ? {
            candidateName: resumeContext.candidateName || undefined,
            headline: resumeContext.headline || undefined,
            skills: isGenMode ? (resumeContext.skills || []) : undefined,
            fullResumeSummary: resumeContext.fullResumeSummary || undefined,
            outputLanguage: resumeContext.outputLanguage || undefined,
            preserveExistingContent: resumeContext.preserveExistingContent || undefined,
            experience: resumeContext.experience?.length ? resumeContext.experience : undefined,
          }
        : undefined,
      outputLanguage: resumeContext?.outputLanguage || 'en',
    };
    
    console.log(`[SectionOptimize] 📤 Calling edge function for "${section.title}"`, {
      model: RESUME_OPTIMIZE_MODEL,
      contentLength: effectiveContent.length,
      hasJobDescription: Boolean(job.description),
      jobDescLength: job.description?.length ?? 0,
      skillsCount: job.skills?.length ?? 0,
    });
    
    const response = await fetch(`${supabaseUrl}/functions/v1/optimize-resume`, {
      method: 'POST',
      signal,
      headers,
      body: JSON.stringify(requestPayload),
    });

    if (!response.ok) {
      const errText = await response.text();
      console.error(`[SectionOptimize] ❌ Edge function error for "${section.title}":`, {
        status: response.status,
        error: errText.slice(0, 300),
      });
      throw new Error(`Edge function ${response.status}: ${errText.slice(0, 200)}`);
    }

    const data = await response.json();
    console.log(`[SectionOptimize] 📥 Edge function response for "${section.title}":`, {
      success: data.success,
      hasOptimizedContent: Boolean(data.optimizedContent),
      optimizedContentLength: data.optimizedContent?.length ?? 0,
      responseKeys: Object.keys(data),
      // Show first 200 chars of optimized content for comparison
      optimizedPreview: data.optimizedContent?.slice(0, 200) ?? '(empty)',
      // Debug info from edge function (if available)
      _debug: data._debug ?? null,
    });
    
    let optimized = stripResumeSectionFence((data.optimizedContent || '').trim());
    
    // Log if content wasn't changed (indicates potential edge function issue)
    const contentUnchanged = optimized === section.content || optimized.length === section.content.length;
    if (contentUnchanged) {
      console.warn(`[SectionOptimize] ⚠️ "${section.title}" content UNCHANGED after optimization!`, {
        originalLength: section.content.length,
        optimizedLength: optimized.length,
        exactMatch: optimized === section.content,
        originalPreview: section.content.slice(0, 100),
        optimizedPreview: optimized.slice(0, 100),
        hint: 'Edge function likely hit an error and returned original content',
      });
    } else {
      console.log(`[SectionOptimize] ✅ "${section.title}" successfully optimized:`, {
        originalLength: section.content.length,
        optimizedLength: optimized.length,
        changeRatio: ((optimized.length / section.content.length) * 100).toFixed(1) + '%',
      });
    }

    // ── Client-side hallucination guard ─────────────────────────────────────
    const targetCo = job.company || '';
    if (targetCo && isExpSection) {
      const coInOriginal = new RegExp(targetCo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i').test(
        section.content,
      );
      if (!coInOriginal) {
        // Strip any line or block that starts with the target company name
        const escapedCo = targetCo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const blockRe = new RegExp(
          `(?:^|\\n)\\*{0,2}${escapedCo}\\*{0,2}[^\\n]*(?:\\n(?![A-Z\\*•])[^\\n]*)*`,
          'gi',
        );
        const cleaned = optimized.replace(blockRe, '').replace(/\n{3,}/g, '\n\n').trim();
        if (cleaned.length > 80) optimized = cleaned;
      }
    }

    // Strip "Current Role: None" artifacts
    optimized = optimized
      .replace(/^(Current|Previous)\s+(Role|Position|Title):\s*None\s*$/im, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    // Debit for each successful LLM section call (fire-and-forget)
    void ledger.resumeSectionOptimize(section.title, { job_title: job.title });

    return {
      title: section.title,
      originalContent: section.content,
      optimizedContent: optimized.length > 20 ? optimized : section.content,
    };
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    const errorMsg = (err as Error).message ?? 'Unknown error';
    console.error(`[SectionOptimize] Failed for "${section.title}":`, err);

    // Instead of silently returning original content, include a marker so the
    // canvas/UI can detect the failure and inform the user.
    return {
      title: section.title,
      originalContent: section.content,
      optimizedContent: section.content,
      skipped: true,
      error: errorMsg,
    } as SectionResult;
  }
}
